import React from 'react'
import useCurrencyExchange from 'src/hooks/useCurrencyExchange'
import { NumericFormat, NumericFormatProps } from 'react-number-format'
import { TMoney, Currency } from 'src/types'
import currencies from 'src/data/currencies'
import { CONVERSION_FEE_MULTIPLIER } from 'src/lib/constants'
import { round } from 'lodash-es';

type Props = {
  amount: TMoney;
  toCurrency?: Currency;
  className?: string;
  hasConversionFee?: boolean;
  decimalScale?: number;
  conversionFeeRate?: number;
  compact?: boolean;
} & NumericFormatProps

const COMPACT_UNITS = [
  { value: 1e6, symbol: 'M' },
  { value: 1e3, symbol: 'k' },
]

// 1000000 -> { value: 1, symbol: 'M' }, 1500 -> { value: 1.5, symbol: 'k' }, 999 -> { value: 999, symbol: '' }
const compactAmount = (value: number, decimalScale: number) => {
  const unit = COMPACT_UNITS.find(u => round(Math.abs(value) / u.value, decimalScale) >= 1)
  return unit
    ? { value: round(value / unit.value, decimalScale), symbol: unit.symbol }
    : { value, symbol: '' }
}

const Money: React.FC<Props> = ({ amount, toCurrency, hasConversionFee, decimalScale, conversionFeeRate, compact, className = '', ...rest }) => {
  const currencyExchange = useCurrencyExchange()
  if (!amount) return <></>

  let actualAmount = currencyExchange({ amount: amount, toCurrency: toCurrency })
  let currentCurrency = currencies.find(o => o.code === actualAmount.currency)
  const conversionFeeMultiplier = conversionFeeRate ? (Number(conversionFeeRate) + 1) : CONVERSION_FEE_MULTIPLIER
  actualAmount = {
    amount: round(
      hasConversionFee ? Number(actualAmount.amount) * conversionFeeMultiplier : Number(actualAmount.amount),
      2
    ),
    currency: actualAmount.currency
  }

  if (!currentCurrency) return <></>

  const finalDecimalScale = typeof decimalScale === 'number' ? decimalScale : (compact ? 1 : Number(currentCurrency.exponent))
  const compacted = compact
    ? compactAmount(parseFloat(String(actualAmount.amount)), finalDecimalScale)
    : { value: parseFloat(String(actualAmount.amount)), symbol: '' }

  return (
    <NumericFormat
      value={compacted.value}
      displayType="text"
      className={className}
      thousandSeparator={currentCurrency.thousandsSeparator}
      decimalSeparator={currentCurrency.decimalMark}
      decimalScale={finalDecimalScale}
      prefix={currentCurrency.prefix ? currentCurrency.prefix : ''}
      suffix={compacted.symbol + (currentCurrency.suffix ? currentCurrency.suffix : '')}
      {...rest}
    />
  )
}

export default Money
